import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PageHeader, Badge } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { listTickets } from '@/data/support';
import {
  isUnprovisioned,
  toTicketRow,
  STATUS_LABEL,
  TICKET_STATUSES,
  type TicketStatus,
} from '@/data/support-model';

/* /sales/support — the staff Support inbox (SCAFFOLD). A workspace-scoped list of
 * tickets with an optional status filter. The Support module ships DARK behind an
 * owner-gated DDL, so a 503 { support_unprovisioned } is rendered as a calm
 * "not turned on yet" panel — never a red error. */
export function SupportInboxPage() {
  const [status, setStatus] = useState<TicketStatus | 'all'>('all');
  const { data: tickets, isPending, error, refetch } = useQuery({
    queryKey: ['support', 'tickets', status],
    queryFn: () => listTickets(status === 'all' ? undefined : status),
    retry: false,
  });

  return (
    <div>
      <PageHeader
        title="Support"
        subtitle="Tickets from your partners and customers — one source of truth, workspace-scoped."
        actions={
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as TicketStatus | 'all')}
            className="h-control rounded-md border border-border-default bg-surface px-2 text-sm text-text-primary"
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            {TICKET_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        }
      />

      {isPending ? (
        <LoadingState rows={6} />
      ) : error ? (
        isUnprovisioned(error) ? (
          <EmptyState
            title="Support isn’t switched on yet"
            teach="The Support inbox is built and ready, but its database tables haven’t been provisioned. Once the owner applies the approved migration, tickets will appear here automatically — nothing else is needed."
          />
        ) : (
          <ErrorState error={error} onRetry={() => void refetch()} />
        )
      ) : tickets.length === 0 ? (
        <EmptyState
          title="No tickets yet"
          teach="When a partner submits a request from your site, or you open one on their behalf, it lands here — deduped, with the whole conversation in one thread."
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr className="border-b border-border-subtle bg-inset/50 text-left text-[11px] uppercase tracking-wide text-text-tertiary">
                  <th className="px-3 py-2 font-semibold">Subject</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Priority</th>
                  <th className="px-3 py-2 font-semibold">Partner</th>
                  <th className="px-3 py-2 font-semibold">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(toTicketRow).map((row) => (
                  <tr key={row.id} className="border-b border-border-subtle last:border-0 hover:bg-hover">
                    <td className="px-3 py-2.5">
                      <Link
                        to={`/sales/support/${row.id}`}
                        className="text-sm font-medium text-text-primary hover:underline"
                      >
                        {row.subject}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge tone={row.statusTone}>{row.statusLabel}</Badge>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-text-secondary">{row.priorityLabel}</td>
                    <td className="px-3 py-2.5 text-xs text-text-tertiary">{row.partnerRef}</td>
                    <td className="px-3 py-2.5 text-xs text-text-tertiary">{row.lastMessageAt || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
